import { formatMoney, type ActualDto, type BillingDto, type PaymentKind } from '@nsb/core';
import { useState } from 'react';
import { api, errorText, queryClient, useApi } from '../../api.ts';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { MoneyInput } from '../../components/MoneyInput.tsx';
import { Button, ErrorLine, Field, LoadError, Loading, Page, Section, TextInput, Title } from '../../components/ui.tsx';
import { ProjectCrumb } from './common.tsx';
import { useProject, useProjectId } from './hooks.ts';
import { easternToday } from '../../days.ts';

/**
 * Billing (design study 04): the money bar's two sub-bars, entered by hand
 * until Xero reports them (phase 4). Billed is what has been invoiced to the
 * client, at the client price; money in is what the client has paid, a
 * deposit or a retainer with it and a refund against it. A bill says nothing
 * of what it was for until the estimate returns on the steps; an older one
 * keeps what it said. Mistakes are voided, never deleted.
 */
export function Spent() {
  const projectId = useProjectId();
  const { data: project } = useProject(projectId);
  const { data, error } = useApi<BillingDto>(['project', projectId, 'billing'], `/projects/${projectId}/billing`);
  useFeedbackRecord(projectId, project ? { type: 'spent', id: projectId, label: `${project.name} — billing` } : null);
  if (error) return <LoadError error={error} />;
  if (!data) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }
  // What they come to, in the money bar's own sentence: one rule for the sums, here and on the bar.
  const unpaid = (data.costBar.bar?.unpaidCents ?? 0) > 0;
  return (
    <Page wide>
      <ProjectCrumb project={project} />
      <Title>Billing</Title>
      <p className={`mb-2 max-w-measure text-lg ${unpaid ? 'text-alert' : ''}`}>{data.costBar.sentence}</p>
      <p className="mb-8 max-w-measure text-sm text-ink-3">At the client price. Bills and money in are entered by hand.</p>
      <div className="grid gap-x-16 gap-y-12 lg:grid-cols-2 lg:items-start">
        <section aria-labelledby="billed-head">
          <h2 id="billed-head" className="mb-3 font-display text-[1.375rem]">
            Billed
          </h2>
          <AddBill projectId={projectId} />
          {data.bills.length ? (
            <Section title="Bills">
              <ul className="divide-y divide-rule">
                {data.bills.map((a) => (
                  <EntryRow
                    key={a.id}
                    projectId={projectId}
                    path="actuals"
                    entry={{ id: a.id, on: a.occurredOn, cents: a.amountCents, first: a.reference, second: billFor(a), note: a.note, voidedAt: a.voidedAt, voidReason: a.voidReason }}
                  />
                ))}
              </ul>
            </Section>
          ) : null}
        </section>
        <section aria-labelledby="in-head">
          <h2 id="in-head" className="mb-3 font-display text-[1.375rem]">
            Money in
          </h2>
          <AddPayment projectId={projectId} />
          {data.payments.length ? (
            <Section title="Received">
              <ul className="divide-y divide-rule">
                {data.payments.map((p) => (
                  <EntryRow
                    key={p.id}
                    projectId={projectId}
                    path="payments"
                    entry={{ id: p.id, on: p.receivedOn, cents: p.kind === 'refund' ? -p.amountCents : p.amountCents, first: p.reference, second: KIND_WORDS[p.kind], note: p.note, voidedAt: p.voidedAt, voidReason: p.voidReason }}
                  />
                ))}
              </ul>
            </Section>
          ) : null}
        </section>
      </div>
    </Page>
  );
}

const KIND_WORDS: Record<PaymentKind, string> = { payment: 'Payment', deposit: 'Deposit', retainer: 'Retainer', refund: 'Refund' };

/** What an older bill said it was for: its code and the trade that prints under it, else the code alone. New bills say nothing. */
function billFor(a: ActualDto): string | null {
  if (a.costCode === null) return null;
  return a.costCodeName ? `For ${a.costCode} ${a.costCodeName}` : `For ${a.costCode}`;
}

const today = easternToday;

function AddBill({ projectId }: { projectId: string }) {
  const [date, setDate] = useState(today);
  const [cents, setCents] = useState<number | null>(null);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [key, setKey] = useState(0);
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/projects/${projectId}/actuals`, { body: { occurredOn: date, amountCents: cents, reference, note } });
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setCents(null);
      setReference('');
      setNote('');
      setKey((k) => k + 1);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="panel mb-6 p-4 sm:p-5" key={key}>
      <div className="grid gap-x-5 sm:grid-cols-2">
        <Field label="Date">{(id) => <TextInput id={id} type="date" value={date} onChange={(e) => setDate(e.target.value)} />}</Field>
        <Field label="Amount">{(id) => <MoneyInput id={id} value={cents} onChange={setCents} />}</Field>
      </div>
      <div className="grid gap-x-5 sm:grid-cols-2">
        <Field label="Invoice number">{(id) => <TextInput id={id} value={reference} onChange={(e) => setReference(e.target.value)} />}</Field>
        <Field label="Note">{(id) => <TextInput id={id} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
      </div>
      <ErrorLine error={err} />
      <Button onClick={() => void save()} busy={busy} disabled={!cents}>
        Record the bill
      </Button>
    </div>
  );
}

function AddPayment({ projectId }: { projectId: string }) {
  const [date, setDate] = useState(today);
  const [cents, setCents] = useState<number | null>(null);
  const [kind, setKind] = useState<PaymentKind>('payment');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [key, setKey] = useState(0);
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/projects/${projectId}/payments`, { body: { receivedOn: date, amountCents: cents, kind, reference, note } });
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setCents(null);
      setKind('payment');
      setReference('');
      setNote('');
      setKey((k) => k + 1);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="panel mb-6 p-4 sm:p-5" key={key}>
      <fieldset className="mb-5">
        <legend className="label mb-1">What it is</legend>
        <div className="inline-flex flex-wrap overflow-hidden rounded-[2px] border border-rule-strong" role="radiogroup" aria-label="What it is">
          {(Object.keys(KIND_WORDS) as PaymentKind[]).map((k, i) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={kind === k}
              onClick={() => setKind(k)}
              className={`min-h-11 px-3 text-sm ${i ? 'border-l border-rule-strong' : ''} ${kind === k ? 'bg-ink text-paper' : 'text-ink-2 hover:bg-inset'}`}
            >
              {KIND_WORDS[k]}
            </button>
          ))}
        </div>
        <p className="meta mt-1">
          {kind === 'refund'
            ? 'Money given back to the client: it comes off what is in.'
            : kind === 'payment'
              ? 'Paid against a bill.'
              : 'Paid ahead: it runs on under the next bands until bills use it up.'}
        </p>
      </fieldset>
      <div className="grid gap-x-5 sm:grid-cols-2">
        <Field label="Date">{(id) => <TextInput id={id} type="date" value={date} onChange={(e) => setDate(e.target.value)} />}</Field>
        <Field label="Amount">{(id) => <MoneyInput id={id} value={cents} onChange={setCents} />}</Field>
        <Field label="Reference">{(id) => <TextInput id={id} value={reference} onChange={(e) => setReference(e.target.value)} />}</Field>
        <Field label="Note">{(id) => <TextInput id={id} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
      </div>
      <ErrorLine error={err} />
      <Button onClick={() => void save()} busy={busy} disabled={!cents}>
        Record it
      </Button>
    </div>
  );
}

interface Entry {
  id: string;
  on: string;
  /** Negative for a refund. */
  cents: number;
  first: string | null;
  second: string | null;
  note: string | null;
  voidedAt: string | null;
  voidReason: string | null;
}

function EntryRow({ projectId, path, entry: a }: { projectId: string; path: 'actuals' | 'payments'; entry: Entry }) {
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  return (
    <li className={`py-3 ${a.voidedAt ? 'text-ink-3' : ''}`}>
      <div className="flex items-baseline justify-between gap-4">
        <span className="min-w-0">
          <span className="num mr-3 text-sm text-ink-3">{a.on}</span>
          {a.second ? <span className="mr-2">{a.second}</span> : null}
          {a.first ? <span className="num mr-2 text-sm text-ink-2">{a.first}</span> : null}
          {a.note ? <span className="text-ink-2">{a.note}</span> : null}
        </span>
        <span className={`num shrink-0 ${a.voidedAt ? 'line-through' : ''}`}>{a.cents < 0 ? `−${formatMoney(-a.cents)}` : formatMoney(a.cents)}</span>
      </div>
      {a.voidedAt ? (
        <p className="text-sm">Void: {a.voidReason}</p>
      ) : voiding ? (
        <div className="mt-2 flex flex-wrap items-end gap-4">
          <div className="min-w-56 flex-1">
            <TextInput aria-label="Why it is void" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <Button
            tone="danger"
            disabled={!reason.trim()}
            onClick={async () => {
              try {
                await api(`/projects/${projectId}/${path}/${a.id}/void`, { body: { reason } });
                await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
              } catch (e) {
                setErr(errorText(e));
              }
            }}
          >
            Void it
          </Button>
        </div>
      ) : (
        <button type="button" className="link mt-1 text-sm text-ink-3" onClick={() => setVoiding(true)}>
          Void
        </button>
      )}
      <ErrorLine error={err} />
    </li>
  );
}

